import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import LinkPreviewBaseLayout from "../LinkPreview/LinkPreviewBaseLayout";
import LinkPreviewModernLayout from "../LinkPreview/LinkPreviewModernLayout";
import LinkPreviewAdvancedLayout from "../LinkPreview/LinkPreviewAdvancedLayout"
import { Upload, X } from "lucide-react";
import { PlatformPills } from "../components/PlatformPills";
import AddFieldModal from "../components/AddFieldModal";
import URLActionBar from "../components/URLActionBar"
import { SketchPicker } from "react-color";
import { useSelector } from "react-redux";
import TrackSelectionModal from './components/TrackSelectionModal'
import { SocialPlatformModal } from "../PlatformModals/SocialPlatformModal";
import { StreamingPlatformModal } from "../PlatformModals/StreamingPlatformModal";
import { showToast } from "../../../../helper/ShowToast";
import { ToastContainer } from "react-toastify";
import LinkAnalytics from "../components/LinkAnalytics";
import { RightPreviewSkeleton } from "../LinkSkeletons";
import { socialPlatformsArray } from "../constants/socialPlatforms";
import { MUSIC_STREAMING_PLATFORMS as streamingPlatformsArray } from "../constants/streamingPlatforms";
import {
    MusicSectionSkeleton,
    PlatformSectionSkeleton,
    LayoutBackgroundSkeleton
} from "./components/Skeletons";
import {
    useCreateMusicLink,
    useSmartLinkDetails,
    useUpdateSmartLink
} from "../../../../queries/artistManger/smartLink.query";
import AppTour from "../../../../components/AppTour/AppTour";
import { musicLinkTourSteps } from "../../../../components/AppTour/ModuleTourSteps";
import { suggestMusicShortIdApi } from "../../../../api/artistManager/smartLink.api";
import ToggleSwitch from "../../../../components/ToggleSwitch";

const MUSIC_LINK_DOMAIN = "https://upnc.me";

export default function MusicLink() {
    const [isOpen, setIsOpen] = useState(false);
    const [chosenRelease, setChosenRelease] = useState(null);
    const [chosenAutoplayTrack, setChosenAutoplayTrack] = useState(null);
    const artistDetails = useSelector(state => state.artistDetails);
    const { artistInfo } = artistDetails;
    const artistManagerData = useSelector(state => state.artistManager);
    const { streamingStore } = artistManagerData
    const navigate = useNavigate();
    const { smartLinkId } = useParams();
    const location = useLocation();
    const isEditMode = Boolean(smartLinkId);
    const [runTour, setRunTour] = useState(false)
    const [emailCollectionEnabled, setEmailCollectionEnabled] = useState(true);
    const [emailButtonText, setEmailButtonText] = useState("Subscribe");

    const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
    const [isStreamingModalOpen, setIsStreamingModalOpen] = useState(false);
    const [openForm, setOpenForm] = useState(false)
    // const [selectedStyle, setSelectedStyle] = useState("pill");
    const [selectedBackground, setSelectedBackground] = useState("color");
    const [selectedColor, setSelectedColor] = useState("#5CBDC7")
    const [expandedPill, setExpandedPill] = useState(null);
    const [expandedStreamingPill, setExpandedStreamingPill] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeTab, setActiveTab] = useState("Build");
    const tabs = ["Build", "Analytics"];
    const [bgImagePreview, setBgImagePreview] = useState("");
    const [bgImageFile, setBgImageFile] = useState(null);
    const [bgImageFileName, setBgImageFileName] = useState("");
    const fileInputRef = useRef(null);

    const [socialPlatforms, setSocialPlatforms] = useState(socialPlatformsArray);
    const [streamingPlatforms, setStreamingPlatforms] = useState(streamingPlatformsArray);
    const [pageLayout, setPageLayout] = useState('modern_layout')
    const [shortId, setShortId] = useState('')
    const [shortIdEdited, setShortIdEdited] = useState(false)
    const [previewConfig, setPreviewConfig] = useState({
        enabled: false,
        storeId: "custom",
        url: ""
    });

    const {
        data: smartLinkDetails,
        isLoading: linkDetailsLoading
    } = useSmartLinkDetails(smartLinkId, isEditMode);

    const {
        mutate: createMusicLink,
        isPending: isCreating,
    } = useCreateMusicLink({ navigate });

    const {
        mutate: updateSmartLink,
        isPending: isUpdating,
    } = useUpdateSmartLink();

    const isLoading = isCreating || isUpdating;


    // BUILD LINK MAP from artistInfo.links
    const buildLinkMap = (links = []) => {
        const map = {};
        links.forEach(item => {
            if (item?.source && item?.url) {
                map[item.source?.toLowerCase()] = item.url;
            }
        });
        return map;
    };

    const buildStoreMap = (store = []) => {
        const map = {};
        store.forEach(item => {
            if (item?.storeId) {
                map[item.storeId?.toLowerCase()] = item.url;
            }
        });
        return map;
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("tab") === "analytics") {
            setActiveTab("Analytics");
        }
    }, [location.search]);

    useEffect(() => {
       if(location.hash){
        setRunTour(true)
       } 
    }, [location.hash])

    // SYNC artistInfo.links → social & streaming platforms
    useEffect(() => {
        if (isEditMode) return; //block in edit
        if (!artistInfo?.links) return;
        const linkMap = buildLinkMap(artistInfo.links);
        setSocialPlatforms(prev =>
            prev.map(platform => {
                const match = linkMap[platform.name.toLowerCase()];
                return match
                    ? { ...platform, url: match }
                    : platform;
            })
        );
    }, [artistInfo, isEditMode]);

    useEffect(() => {
        if (isEditMode) return; //block in edit
        if (!streamingStore?.length) return;

        const linkMap = buildStoreMap(streamingStore);

        const updated = streamingPlatforms.map(platform => {
            const match = linkMap[platform.id.toLowerCase()];

            return {
                ...platform,
                url: match || "",
                enabled: !!match
            };
        });
        const orderMap = new Map(
            streamingStore.map((s, index) => [s.storeId.toLowerCase(), index])
        );
        const sorted = [...updated].sort((a, b) => {
            const aIndex = orderMap.has(a.id) ? orderMap.get(a.id) : 999;
            const bIndex = orderMap.has(b.id) ? orderMap.get(b.id) : 999;
            return aIndex - bIndex;
        });
        setStreamingPlatforms(sorted);
    }, [streamingStore, isEditMode]);

    useEffect(() => {
        if (isEditMode) return;
        setShortIdEdited(false);
    }, [chosenRelease?.id, chosenAutoplayTrack?.id, isEditMode]);

    useEffect(() => {
        if (isEditMode || shortIdEdited) return;

        const songTitle = chosenRelease?.name?.trim();
        const releaseType = chosenRelease?.type;
        const needsAutoplay = releaseType === "album" || releaseType === "playlist";
        const autoplayName = chosenAutoplayTrack?.name?.trim();

        if (!songTitle || (needsAutoplay && !autoplayName)) {
            setShortId("");
            return;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const result = await suggestMusicShortIdApi({
                    songTitle,
                    releaseType,
                    autoplayTrackSongName: autoplayName || ""
                });
                if (!cancelled) setShortId(result?.data?.shortId || "");
            } catch {
                if (!cancelled) setShortId("");
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [
        isEditMode,
        shortIdEdited,
        chosenRelease?.id,
        chosenRelease?.name,
        chosenRelease?.type,
        chosenAutoplayTrack?.id,
        chosenAutoplayTrack?.name
    ]);

    useEffect(() => {
        if (!isEditMode) return;
        if (!smartLinkDetails || Object.keys(smartLinkDetails).length === 0) return;

        // ===== SONG =====
        setChosenRelease({
            id: smartLinkDetails.trackId || smartLinkDetails.albumId || smartLinkDetails.playlistId,
            name: smartLinkDetails.title,
            type: smartLinkDetails.releaseType,
            images: [{ url: smartLinkDetails.image }],
            album: { images: [{ url: smartLinkDetails.image }] }
        });

        if (smartLinkDetails.autoplayTrackId) {
            setChosenAutoplayTrack({
                id: smartLinkDetails.autoplayTrackId,
                name: smartLinkDetails.autoplayTrackSongName
            });
        }
        setShortId(smartLinkDetails?.shortId)

        const c = smartLinkDetails?.customization;

        // ===== BASIC =====
        setEmailCollectionEnabled(c?.emailCollection?.enabled ?? true);
        setEmailButtonText(c?.emailCollection?.actionName || "Subscribe");
        setSelectedBackground(c?.backgroundType);
        setSelectedColor(c?.backgroundColor);
        setPageLayout(c?.pageLayout);

        if (c?.backgroundImageUrl) setBgImagePreview(c.backgroundImageUrl);

        // ===== SOCIAL =====
        const socialLinks = c?.socialIcons?.socialLinks || {};

        setSocialPlatforms(prev =>
            prev.map(p => {
                const url = socialLinks[p.name.toLowerCase()];
                return {
                    ...p,
                    url: url || "",
                    enabled: !!url
                };
            })
        );

        // ===== STREAMING (WITH ORDER FIX) =====
        const stores = c?.stores || [];

        const storeMap = new Map(
            stores.map(s => [s.storeId.toLowerCase(), s.url])
        );

        const updated = streamingPlatforms.map(p => {
            const match = storeMap.get(p.id.toLowerCase());

            return {
                ...p,
                url: match || "",
                enabled: !!match
            };
        });

        const orderMap = new Map(
            stores.map((s, index) => [s.storeId.toLowerCase(), index])
        );

        const sorted = [...updated].sort((a, b) => {
            const aIndex = orderMap.has(a.id) ? orderMap.get(a.id) : 999;
            const bIndex = orderMap.has(b.id) ? orderMap.get(b.id) : 999;
            return aIndex - bIndex;
        });

        setStreamingPlatforms(sorted);

        // ===== PREVIEW =====
        if (c?.preview) {
            setPreviewConfig({
                enabled: c.preview.enabled ?? false,
                storeId: c.preview.storeId || "custom",
                url: c.preview.url || ""
            });
        } else {
            setPreviewConfig({
                enabled: false,
                storeId: "custom",
                url: ""
            });
        }

    }, [smartLinkDetails, isEditMode]);

    const goBack = () => navigate('/admin/artist-manager/link-and-fan-hub');
    // const handleSelectStyle = (value) => setSelectedStyle(value);
    const handleSelectBackground = (value) => setSelectedBackground(value);

    const handlePillClick = (id) => {
        setExpandedPill(expandedPill === id ? null : id);
    };

    const handleStreamingPillClick = (id) => {
        setExpandedStreamingPill(expandedStreamingPill === id ? null : id);
    };

    const socialPlatformsUrlChange = (id, newUrl) => {
        setSocialPlatforms(
            socialPlatforms.map((p) => (p.id === id ? { ...p, url: newUrl } : p))
        );
    };

    const streamingPlatformsUrlChange = (id, newUrl) => {
        setStreamingPlatforms(prev =>
            prev.map((p) =>
                p.id === id
                    ? {
                        ...p,
                        url: newUrl,
                        enabled: !!newUrl
                    }
                    : p
            )
        );
    };

    const handleCollapse = (e, id) => {
        e.stopPropagation();
        setExpandedPill(null);
    };

    const handleStreamingCollapse = (e, id) => {
        e.stopPropagation();
        setExpandedStreamingPill(null);
    };

    const togglePlay = () => {
        setIsPlaying(!isPlaying);
    };
    const userHasAnyUrl = socialPlatforms.some(p => p.url && p.url.trim() !== "");
    const activePlatforms = userHasAnyUrl
        ? socialPlatforms
        : socialPlatformsArray;

    const openBgFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleBgImageChange = (e) => {
        const file = e.target.files?.[0];

        if (!file || !file.type.startsWith("image/")) return;

        setBgImageFile(file);
        setBgImageFileName(file.name);

        const previewURL = URL.createObjectURL(file);
        setBgImagePreview(previewURL);
    };

    const removeBgImage = () => {
        if (bgImagePreview) URL.revokeObjectURL(bgImagePreview);

        setBgImageFile(null);
        setBgImagePreview("");
        setBgImageFileName("");
    };

    const buildEnabledStores = () => {
        return streamingPlatforms
            .filter(p => p.enabled && p.url) // only enabled AND has URL
            .map(p => ({
                storeId: String(p.id).toLowerCase(),
                actionName: p.actionName || "Play",
            }));
    };

    const buildStores = () => {
        return streamingPlatforms
            .filter(p => p.enabled && p.url?.trim())
            .map(p => ({
                storeId: String(p.id).toLowerCase(),
                url: p.url
            }));
    };

    const handlePublish = async () => {
        // Release is required
        if (!chosenRelease?.id) {
            showToast("warning", "Please select a release.");
            return;
        }
        // Autoplay track required for album/playlist
        if (
            (chosenRelease.type === "album" || chosenRelease.type === "playlist") &&
            !chosenAutoplayTrack?.id
        ) {
            showToast("warning", "Please select an autoplay track.");
            return;
        }

        // Background = image → file must exists
        if (selectedBackground === "image" && !bgImageFile) {
            showToast("warning", "Please upload a background image.");
            return;
        }

        //Background = color → color required
        if (selectedBackground === "color" && !selectedColor) {
            showToast("warning", "Please select a background color.");
            return;
        }


        // Must have at least one enabled store
        if (!streamingPlatforms.some(p => p.url)) {
            showToast("warning", "Please add at least one streaming platform.");
            return;
        }
        if (previewConfig.enabled) {
            const url = previewConfig.url?.trim();
            if (!url) {
                showToast("warning", "Preview URL is required");
                return;
            }

            const isValid =
                url.endsWith(".mp3") ||
                url.endsWith(".m4a")

            if (!isValid) {
                showToast(
                    "error",
                    "Preview must be MP3, m4a link"
                );
                return;
            }
        }
        const formData = new FormData();
        // ----- SONG INFO -----
        formData.append("songTitle", chosenRelease?.name || "");
        formData.append("trackId", chosenRelease?.type === "track" ? chosenRelease.id : "");
        formData.append("albumId", chosenRelease?.type === "album" ? chosenRelease.id : "");
        formData.append("playlistId", chosenRelease?.type === "playlist" ? chosenRelease.id : "");
        formData.append("autoplayTrackId", chosenAutoplayTrack?.id || "");
        formData.append("autoplayTrackSongName", chosenAutoplayTrack?.name || "");
        if (shortId?.trim()) {
            formData.append("shortId", shortId.trim().toLowerCase());
        }

        if (chosenRelease?.type === 'track') {
            formData.append("songImageUrl", chosenRelease?.album.images[0].url);
        } else if (chosenRelease?.type === 'album') {
            formData.append("songImageUrl", chosenRelease?.images[0].url);
        } else if (chosenRelease?.type === 'playlist') {
            formData.append("songImageUrl", chosenRelease?.images[0].url);
        }

        const enabledStores = buildEnabledStores();
        const stores = buildStores();
        // ----- CUSTOMIZATION -----
        const customization = {
            // style: selectedStyle,
            backgroundType: selectedBackground,
            backgroundColor: selectedColor,
            enabledStores: enabledStores,
            stores: stores,
            preview: {
                enabled: previewConfig.enabled,
                storeId: "custom",
                url: previewConfig.enabled ? previewConfig.url : ""
            },
            emailCollection: {
                enabled: emailCollectionEnabled,
                actionName: emailButtonText,
            },
            socialIcons: {
                enabled: true,
                socialLinks: socialPlatforms.reduce((acc, p) => {
                    if (p.url) acc[p.name.toLowerCase()] = p.url;
                    return acc;
                }, {})
            },
            pageLayout: pageLayout,
        };

        formData.append("customization", JSON.stringify(customization));

        // ----- BACKGROUND FILE -----
        if (selectedBackground === "image" && bgImageFile) {
            formData.append("backgroundImage", bgImageFile);
        }
        // call create music mutation
        createMusicLink(formData);

    };

    const handleUpdate = async () => {
        if (previewConfig.enabled) {
            const url = previewConfig.url?.trim();
            if (!url) {
                showToast("warning", "Preview URL is required");
                return;
            }

            const isValid =
                url.endsWith(".mp3") ||
                url.endsWith(".m4a")

            if (!isValid) {
                showToast(
                    "error",
                    "Preview must be MP3, MP4a link"
                );
                return;
            }
        }
        const formData = new FormData();
        const isShortIdChanged = shortId !== smartLinkDetails.shortId;
        if (isShortIdChanged) {
            if (!/^[a-z0-9-]+$/.test(shortId)) {
                showToast("warning", "Invalid short link format");
                return;
            }

            if (shortId.length < 3) {
                showToast("warning", "Short link too short");
                return;
            }
        }
        if (isShortIdChanged && shortId) {
            formData.append("shortId", shortId);
        }
        // We do NOT send song fields again (cannot edit song)
        const customization = {
            backgroundType: selectedBackground,
            backgroundColor: selectedColor,
            enabledStores: buildEnabledStores(),
            stores: buildStores(),
            emailCollection: {
                enabled: emailCollectionEnabled,
                actionName: emailButtonText,
            },
            socialIcons: {
                enabled: true,
                socialLinks: socialPlatforms.reduce((acc, p) => {
                    if (p.url) acc[p.name.toLowerCase()] = p.url;
                    return acc;
                }, {})
            },
            pageLayout: pageLayout,
            preview: {
                enabled: previewConfig.enabled,
                storeId: 'custom',
                url: previewConfig.url || ""
            },
        };

        formData.append("customization", JSON.stringify(customization));

        if (selectedBackground === "image" && bgImageFile) {
            formData.append("backgroundImage", bgImageFile);
        }

        updateSmartLink({
            smartLinkId,
            formData
        });
    };

    const socialPlatformsWithUrl = socialPlatforms.filter(
        (p) => p.url && p.url.trim() !== ""
    );

    const streamingPlatformsWithUrl = streamingPlatforms.filter(
        (p) => p.url?.trim() && p.enabled
    );

    const previewData = {
        background: {
            type: selectedBackground, // "color" | "image"
            color: selectedColor,
            image: bgImagePreview,
            fallback: "#5CBDC7"
        },

        release: {
            image:
                chosenRelease?.images?.[0]?.url ||
                chosenRelease?.album?.images?.[0]?.url ||
                require("../../../../assets/images/karan.png"),
            name: chosenRelease?.name || "Track name",
            artist:
                chosenRelease?.artists?.[0]?.name ||
                chosenRelease?.owner?.display_name ||
                "Artist name"
        },

        player: {
            isPlaying,
            onToggle: togglePlay
        },

        socialPlatforms: activePlatforms
            .filter(p => (userHasAnyUrl ? p.url : true))
            .map(p => ({
                id: p.id,
                name: p.name,
                url: p.url || "#",
                icon: p.icon
            })),

        streamingPlatforms: streamingPlatformsWithUrl,
        mode: 'smartlink'
    };
    
    return (
        <>
            <div className="BuildBioLink-Page font-jakarta">
                <ToastContainer />
                <AppTour
                    tourSteps={musicLinkTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                />
                {/* Header */}
                <div className="sticky top-0 shadow z-30 bg-theme_clr1 text-white px-4 py-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <button onClick={goBack} className="shrink-0">
                                <X className="w-5 h-5" />
                            </button>
                            <h1 className="text-sm sm:text-lg font-semibold truncate">
                                {activeTab === "Build" ? `${isEditMode ? "Edit" : "Build a"} Music Link` : "Music Link Analytics"}
                            </h1>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto lg:min-w-0 lg:flex-1 lg:justify-end">
                            {((isEditMode && smartLinkDetails?.domain && shortId) || (!isEditMode && (shortId || shortIdEdited))) && (
                                <div className="w-full sm:w-[350px] lg:w-auto lg:min-w-[350px] lg:max-w-[640px] lg:flex-1">
                                    <URLActionBar
                                        fullLink={isEditMode ? smartLinkDetails?.fullLink : `${MUSIC_LINK_DOMAIN}/${shortId}`}
                                        linkType={'smartlink'}
                                        domain={isEditMode ? smartLinkDetails?.domain : MUSIC_LINK_DOMAIN}
                                        shortId={shortId}
                                        isPreview={!isEditMode}
                                        canEdit={activeTab !== "Analytics"}
                                        onChangeShortId={(newShortId) => {
                                            if (!isEditMode) setShortIdEdited(true);
                                            setShortId(newShortId);
                                        }}
                                    />
                                </div>
                            )}
                            {activeTab === "Build" && (
                                <button
                                    className={`bg-theme_clr2 px-4 sm:px-6 py-2 text-white text-sm sm:text-base font-semibold rounded-full flex items-center justify-center whitespace-nowrap
                                ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                                    onClick={isEditMode ? handleUpdate : handlePublish}
                                    disabled={isLoading}
                                    data-tour="publish"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            {isEditMode ? "Saving..." : "Publishing..."}
                                        </div>
                                    ) : (
                                        isEditMode ? "Save Changes" : "Publish"
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="container">
                    {/* Tabs Menu */}
                    <div className={`lg:w-[calc(100%-374px)] shadow relative z-20 flex border-2 border-theme_clr1 bg-white rounded-b-xl mb-4 px-4`}>
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                disabled={!isEditMode && tab === 'Analytics'}
                                className={`font-semibold border-b-2 border-theme_clr1 mb-[-2px] px-4 py-2 ${(!isEditMode && tab === 'Analytics') ? 'opacity-50' : ''} ${activeTab === tab
                                    ? "border-theme_clr2 text-theme_clr2"
                                    : "text-theme_clr1"
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-col-reverse lg:flex-row lg:justify-between gap-6">
                        {/* LEFT SECTION */}
                        <div className="left-section flex-1 lg:w-[calc(100%-374px)]">
                            {linkDetailsLoading && isEditMode ? (
                                <>
                                    <MusicSectionSkeleton />
                                    <LayoutBackgroundSkeleton />
                                    <PlatformSectionSkeleton title="Streaming Platforms" />
                                    <PlatformSectionSkeleton title="Social Platforms" />
                                </>
                            ) : (
                                <>
                                    {/* Tab Content */}
                                    {activeTab === "Build" && (
                                        <>
                                            {linkDetailsLoading ? (
                                                <MusicSectionSkeleton />
                                            ) : (
                                                < div className="bg-white border-2 border-theme_clr1 rounded-xl p-4 md:p-6 mb-5" data-tour="profile-form">
                                                    <div className="flex flex-wrap items-center justify-between gap-3 text-lg mb-2">
                                                        <span className="font-bold text-theme_clr1">Music</span>
                                                    </div>
                                                    <div
                                                        className="bg-white border border-theme_clr1 flex flex-wrap items-center justify-between gap-3 rounded-xl mb-4 p-4 cursor-pointer"
                                                        onClick={() => setIsOpen(true)}
                                                    >

                                                        {/* LEFT SIDE: Content */}
                                                        <div className="flex items-center gap-3">

                                                            {/* Thumbnail */}
                                                            <img
                                                                src={
                                                                    chosenRelease?.id
                                                                        ? (
                                                                            chosenRelease?.images?.[0]?.url ||
                                                                            chosenRelease?.album?.images?.[0]?.url
                                                                        )
                                                                        : require('../../../../assets/images/karan.png')
                                                                }
                                                                alt="Thumbnail"
                                                                className="h-[70px] w-[70px] rounded-xl object-cover"
                                                            />

                                                            <div className="max-w-[250px]">

                                                                {/* If nothing selected */}
                                                                {!chosenRelease?.id ? (
                                                                    <>
                                                                        <div className="font-bold text-theme_clr1">
                                                                            Select your music or track
                                                                        </div>

                                                                        <div className="font-bold text-theme_clr2 text-base">
                                                                            for Smartlink
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {/* If chosenRelease exists → show details */}
                                                                        <div className="font-bold text-theme_clr1 truncate">
                                                                            {chosenRelease?.name}
                                                                        </div>

                                                                        {/* Type text */}
                                                                        <div className="font-bold text-theme_clr2 text-base truncate">
                                                                            {chosenRelease.type === "track" && (
                                                                                <>
                                                                                    {chosenRelease?.artists?.[0]?.name} • Single
                                                                                </>
                                                                            )}

                                                                            {chosenRelease.type === "album" && (
                                                                                <>
                                                                                    Album • {chosenRelease?.artists?.[0]?.name}
                                                                                </>
                                                                            )}

                                                                            {chosenRelease.type === "playlist" && (
                                                                                <>Playlist</>
                                                                            )}
                                                                        </div>

                                                                        {/* Autoplay Track for album/playlist */}
                                                                        {(chosenRelease.type === "album" ||
                                                                            chosenRelease.type === "playlist") &&
                                                                            chosenAutoplayTrack?.id && (
                                                                                <div className="text-sm text-gray-600 mt-1 truncate">
                                                                                    Autoplay Track:
                                                                                    <span className="font-semibold text-theme_clr2">
                                                                                        {" "}{chosenAutoplayTrack?.name}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* RIGHT SIDE BUTTON */}
                                                        <button
                                                            className="font-bold text-theme_clr2"
                                                            onClick={(e) => {
                                                                e.stopPropagation(); // prevent parent onClick
                                                                if (!isEditMode) setIsOpen(true);
                                                            }}
                                                        >
                                                            {isEditMode ? "Song cannot be changed in edit" : chosenRelease?.id ? "Change" : "Select"}
                                                        </button>
                                                    </div>



                                                    <div className="chip-card-wrapper flex flex-col gap-4">
                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                                            <div className="font-bold sm:min-w-[110px] text-theme_clr1 text-sm sm:text-base">Layout</div>
                                                            <div className="grid grid-cols-3 gap-2 sm:gap-3 flex-1">
                                                                <button
                                                                    type="button"
                                                                    className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                                                        ${pageLayout === "modern_layout"
                                                                            ? "border-theme_clr2 bg-theme_clr2 text-white"
                                                                            : "border-theme_clr1 text-theme_clr1"}`}
                                                                    onClick={() => setPageLayout("modern_layout")}
                                                                >
                                                                    Modern
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                                                        ${pageLayout === "action_page"
                                                                            ? "border-theme_clr2 bg-theme_clr2 text-white"
                                                                            : "border-theme_clr1 text-theme_clr1"}`}
                                                                    onClick={() => setPageLayout("action_page")}
                                                                >
                                                                    Advanced
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                                                        ${pageLayout === "base_layout"
                                                                            ? "border-theme_clr2 bg-theme_clr2 text-white"
                                                                            : "border-theme_clr1 text-theme_clr1"}`}
                                                                    onClick={() => setPageLayout("base_layout")}
                                                                >
                                                                    Base
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                                            <div className="font-bold sm:min-w-[110px] text-theme_clr1 text-sm sm:text-base">Background</div>
                                                            <div className="grid grid-cols-3 gap-2 sm:gap-3 flex-1">
                                                                <button
                                                                    type="button"
                                                                    className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                                                        ${selectedBackground === "blur"
                                                                            ? "border-theme_clr2 bg-theme_clr2 text-white"
                                                                            : "border-theme_clr1 text-theme_clr1"}`}
                                                                    onClick={() => handleSelectBackground("blur")}
                                                                >
                                                                    Blur
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                                                        ${selectedBackground === "color"
                                                                            ? "border-theme_clr2 bg-theme_clr2 text-white"
                                                                            : "border-theme_clr1 text-theme_clr1"}`}
                                                                    onClick={() => handleSelectBackground("color")}
                                                                >
                                                                    Color
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                                                        ${selectedBackground === "image"
                                                                            ? "border-theme_clr2 bg-theme_clr2 text-white"
                                                                            : "border-theme_clr1 text-theme_clr1"}`}
                                                                    onClick={() => handleSelectBackground("image")}
                                                                >
                                                                    Image
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-wrap justify-center items-center gap-2">
                                                            {selectedBackground === "image" && (
                                                                <div className="w-full max-w-full bg-white rounded-2xl shadow-xl p-8">

                                                                    {/* Hidden input MUST be here */}
                                                                    <input
                                                                        type="file"
                                                                        ref={fileInputRef}
                                                                        className="hidden"
                                                                        accept="image/*"
                                                                        onChange={handleBgImageChange}   //This calls handleBgImageChange
                                                                    />

                                                                    {/* IF NO IMAGE SELECTED */}
                                                                    {!bgImagePreview && (
                                                                        <div
                                                                            className="flex flex-col items-center justify-center w-full h-64 
                                                                   border-2 border-dashed border-gray-300 rounded-xl cursor-pointer 
                                                                     hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-300"
                                                                            onClick={openBgFilePicker}    // opens file dialog
                                                                        >
                                                                            <Upload className="w-12 h-12 text-gray-400 mb-3" />
                                                                            <p className="mb-2 text-sm text-gray-500">
                                                                                <span className="font-semibold">Click to upload</span> or drag & drop
                                                                            </p>
                                                                            <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                                                                        </div>
                                                                    )}

                                                                    {/* IF IMAGE SELECTED */}
                                                                    {bgImagePreview && (
                                                                        <div className="relative">
                                                                            <div className="w-full h-64 rounded-xl overflow-hidden bg-gray-100">
                                                                                <img
                                                                                    src={bgImagePreview}
                                                                                    alt="Background"
                                                                                    className="w-full h-full object-contain"
                                                                                />
                                                                            </div>

                                                                            <button
                                                                                onClick={removeBgImage}
                                                                                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 
                                                                                           text-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                                                                            >
                                                                                <X className="w-5 h-5" />
                                                                            </button>

                                                                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                                                                <p className="text-sm text-gray-600 truncate">
                                                                                    <span className="font-semibold">File:</span> {bgImageFileName}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}


                                                            {selectedBackground === "color" &&
                                                                <div>
                                                                    <SketchPicker
                                                                        color={selectedColor}
                                                                        onChangeComplete={(e) => setSelectedColor(e.hex)}
                                                                    />
                                                                </div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {linkDetailsLoading ? (
                                                <>
                                                    <PlatformSectionSkeleton title="Social Platforms" />
                                                    <PlatformSectionSkeleton title="Streaming Platforms" />
                                                </>
                                            ) : (
                                                <div className="flex flex-col gap-4">

                                                    {/* ================= STREAMING ================= */}
                                                    <div className="bg-white border-2 border-theme_clr1 rounded-xl p-2 md:p-4" data-tour="streaming-platforms">

                                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
                                                            <span className="font-bold text-theme_clr1 text-lg">Streaming Platforms</span>

                                                            <button
                                                                onClick={() => setIsStreamingModalOpen(true)}
                                                                className="font-bold text-theme_clr2"
                                                            >
                                                                <i className="bi bi-plus-circle mr-1"></i> Edit Platform
                                                            </button>
                                                        </div>

                                                        <div className="flex flex-wrap gap-2 mb-5">
                                                            {streamingPlatformsWithUrl.slice(0, 3).map((platform) => (
                                                                <PlatformPills
                                                                    key={platform.id}
                                                                    platform={platform}
                                                                    expandedPill={expandedStreamingPill}
                                                                    handlePillClick={handleStreamingPillClick}
                                                                    handleCollapse={handleStreamingCollapse}
                                                                    handleUrlChange={streamingPlatformsUrlChange}
                                                                />
                                                            ))}
                                                        </div>

                                                        <div className="flex flex-wrap gap-2 mb-5">
                                                            {streamingPlatformsWithUrl.slice(3, 6).map((platform) => (
                                                                <PlatformPills
                                                                    key={platform.id}
                                                                    platform={platform}
                                                                    expandedPill={expandedStreamingPill}
                                                                    handlePillClick={handleStreamingPillClick}
                                                                    handleCollapse={handleStreamingCollapse}
                                                                    handleUrlChange={streamingPlatformsUrlChange}
                                                                />
                                                            ))}
                                                        </div>

                                                        <div className="flex flex-wrap gap-2">
                                                            {streamingPlatformsWithUrl.slice(6, 9).map((platform) => (
                                                                <PlatformPills
                                                                    key={platform.id}
                                                                    platform={platform}
                                                                    expandedPill={expandedStreamingPill}
                                                                    handlePillClick={handleStreamingPillClick}
                                                                    handleCollapse={handleStreamingCollapse}
                                                                    handleUrlChange={streamingPlatformsUrlChange}
                                                                />
                                                            ))}
                                                        </div>

                                                    </div>

                                                    {/* ================= PREVIEW SONG ================= */}
                                                    <div className="bg-white border-2 border-theme_clr1 rounded-xl p-4" data-tour="preview-song">

                                                        <div className="flex items-center justify-between mb-4">
                                                            <span className="font-bold text-theme_clr1 text-lg">
                                                                Preview Song
                                                            </span>

                                                            <ToggleSwitch
                                                                size="md"
                                                                onColor="bg-theme_clr2"
                                                                enabled={previewConfig.enabled}
                                                                setEnabled={(val) =>
                                                                    setPreviewConfig(prev => ({
                                                                        ...prev,
                                                                        enabled: val
                                                                    }))
                                                                }
                                                            />
                                                        </div>

                                                        {previewConfig.enabled && (
                                                            <div>

                                                                {/* URL ONLY */}
                                                                <div>
                                                                    <label className="text-sm font-semibold text-theme_clr1">
                                                                        Preview URL{" "}
                                                                        <span className="text-xs text-red-500">
                                                                            (Only .mp3 or .m4a supported)
                                                                        </span>
                                                                    </label>

                                                                    <input
                                                                        type="text"
                                                                        value={previewConfig.url}
                                                                        onChange={(e) =>
                                                                            setPreviewConfig(prev => ({
                                                                                ...prev,
                                                                                url: e.target.value
                                                                            }))
                                                                        }
                                                                        placeholder="Enter preview URL (YouTube / MP3 / SoundCloud)"
                                                                        className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-theme_clr1 placeholder:!text-gray-600"
                                                                    />
                                                                </div>

                                                            </div>
                                                        )}

                                                    </div>

                                                    {/* ================= EMAIL COLLECTION ================= */}
                                                    <div className="bg-white border-2 border-theme_clr1 rounded-xl p-4" data-tour="collect-fan-data">

                                                        <div className="flex items-center justify-between mb-4">
                                                            <span className="font-bold text-theme_clr1 text-lg">
                                                                Collect Data
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center justify-between mb-4">
                                                            <span className="font-bold text-theme_clr1 text-base">
                                                                Enable Email Collection
                                                            </span>

                                                            <ToggleSwitch
                                                                size="md"
                                                                onColor="bg-theme_clr2"
                                                                enabled={emailCollectionEnabled}
                                                                setEnabled={setEmailCollectionEnabled}
                                                            />
                                                        </div>

                                                        {emailCollectionEnabled && (
                                                            <div className="input-cover">
                                                                <label className="font-bold text-theme_clr2 mb-1 block">
                                                                    Email Button Text
                                                                </label>

                                                                <input
                                                                    type="text"
                                                                    className="tw-form-control font-semibold border border-gray-300 rounded-md px-3 py-2"
                                                                    value={emailButtonText}
                                                                    maxLength={20}
                                                                    onChange={(e) =>
                                                                        setEmailButtonText(e.target.value.slice(0, 20))
                                                                    }
                                                                    placeholder="Subscribe"
                                                                />

                                                                <div className="text-xs text-gray-500 mt-1 text-right">
                                                                    {emailButtonText.length}/20
                                                                </div>
                                                            </div>
                                                        )}

                                                    </div>

                                                    {/* ================= SOCIAL ================= */}
                                                    <div className="bg-white border-2 border-theme_clr1 rounded-xl p-2 md:p-4 mb-6" data-tour="social-footer">

                                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
                                                            <span className="font-bold text-theme_clr1 text-lg">Social Footer</span>

                                                            <button
                                                                onClick={() => setIsSocialModalOpen(true)}
                                                                className="font-bold text-theme_clr2"
                                                            >
                                                                <i className="bi bi-plus-circle mr-1"></i> Edit Platform
                                                            </button>
                                                        </div>

                                                        <div className="flex flex-wrap gap-2 mb-5">
                                                            {socialPlatformsWithUrl.slice(0, 3).map((platform) => (
                                                                <PlatformPills
                                                                    key={platform.id}
                                                                    platform={platform}
                                                                    expandedPill={expandedPill}
                                                                    handlePillClick={handlePillClick}
                                                                    handleCollapse={handleCollapse}
                                                                    handleUrlChange={socialPlatformsUrlChange}
                                                                />
                                                            ))}
                                                        </div>

                                                        <div className="flex flex-wrap gap-2">
                                                            {socialPlatformsWithUrl.slice(3).map((platform) => (
                                                                <PlatformPills
                                                                    key={platform.id}
                                                                    platform={platform}
                                                                    expandedPill={expandedPill}
                                                                    handlePillClick={handlePillClick}
                                                                    handleCollapse={handleCollapse}
                                                                    handleUrlChange={socialPlatformsUrlChange}
                                                                />
                                                            ))}
                                                        </div>

                                                    </div>

                                                </div>
                                            )}
                                        </>
                                    )}
                                    {activeTab === "Analytics" && (
                                        <div className="bg-white border-2 border-theme_clr1 rounded-xl p-4 md:p-6 mb-5">
                                            <LinkAnalytics
                                                smartLinkId={smartLinkId}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* RIGHT SECTION (unchanged) */}
                        {linkDetailsLoading && isEditMode ? (
                            <RightPreviewSkeleton />
                        ) : (
                            <div className="right-section w-[350px] mx-auto mt-6" data-tour="mobile-demo">
                                {
                                    pageLayout === "base_layout" ?
                                        <LinkPreviewBaseLayout previewData={previewData} /> :
                                        pageLayout === "action_page" ?
                                            <LinkPreviewAdvancedLayout previewData={previewData} /> :
                                            <LinkPreviewModernLayout previewData={previewData} />
                                }
                            </div>
                        )}
                    </div>
                </div>
            </div >
            <SocialPlatformModal
                isOpen={isSocialModalOpen}
                onClose={() => setIsSocialModalOpen(false)}
                platforms={socialPlatforms}
                onSave={(updated) => setSocialPlatforms(updated)}
            />
            <StreamingPlatformModal
                isOpen={isStreamingModalOpen}
                onClose={() => setIsStreamingModalOpen(false)}
                allPlatforms={streamingPlatforms}
                onSave={(updated) =>
                    setStreamingPlatforms(
                        updated.map(p => ({
                            ...p,
                            enabled: p.url?.trim() ? true : p.enabled
                        }))
                    )
                }
            />
            <AddFieldModal
                isOpen={openForm}
                onClose={() => setOpenForm(false)}
            />
            {
                !isEditMode && (
                    <TrackSelectionModal
                        isOpen={isOpen}
                        onClose={() => setIsOpen(false)}
                        artistInfo={{ spotifyArtistId: artistInfo?.spotifyArtistId, spotifyArtistName: artistInfo?.spotifyArtistName }} // pass as available
                        onSelect={(release, autoplayTrack) => {
                            setChosenRelease(release);
                            setChosenAutoplayTrack(autoplayTrack || null);
                            setIsOpen(false);
                        }}
                        chosenRelease={chosenRelease}
                        chosenAutoplayTrack={chosenAutoplayTrack}
                    />
                )
            }
        </>
    );
}
